import React, { useEffect, useState } from "react";
import { Col, Form, FormControl } from "react-bootstrap";

import { COUNTED, INFINITE } from "constant";
import { useActuatorConfigurationContext } from "context/ActuatorConfigurationContext";
import { Actuator } from "types/Actuator";
import { BlueRadio, LightRadio } from "components/common";

export const RawActuatorCount = ({
    selectedLoopCycle,
    loopCycleOption,
    onChange,
    variant = "default",
}: {
    selectedLoopCycle?: number;
    loopCycleOption: string;
    onChange: ({
        cycleOption,
        cycle_count,
    }: {
        cycleOption: typeof INFINITE | typeof COUNTED;
        cycle_count: number;
    }) => void;
    variant?: "default" | "light";
}) => {
    const Radio = variant === "default" ? BlueRadio : LightRadio;

    return (
        <>
            <Form.Row className="mt-4 mb-4">
                <Col md={2}>
                    <Radio
                        name="loops"
                        id={INFINITE}
                        checked={loopCycleOption === INFINITE}
                        onChange={() => onChange({
                            cycleOption: INFINITE,
                            cycle_count: selectedLoopCycle ?? 0,
                        })}
                        custom
                        type="radio"
                        label={<span className="ml-2">Infinite</span>}
                    />
                </Col>
                <Col md={2}>
                    <Radio
                        name="loops"
                        id={COUNTED}
                        checked={loopCycleOption === COUNTED}
                        onChange={() => onChange({
                            cycleOption: COUNTED,
                            cycle_count: selectedLoopCycle ?? 0,
                        })}
                        custom
                        type="radio"
                        label={<span className="ml-2">Counted</span>}
                    />
                </Col>
            </Form.Row>
            {loopCycleOption === COUNTED ? (
                <Form.Row className="mb-4">
                    <Col md={1}>
                        <Form.Label className="mt-2">Count</Form.Label>
                    </Col>
                    <Col md={7}>
                        <FormControl
                            type="number"
                            min={0}
                            value={selectedLoopCycle}
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                if (e.key === ".") e.preventDefault();
                            }}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const { value } = e.target;
                                const intValue = Math.trunc(Number(value));
                                onChange({
                                    cycleOption: COUNTED,
                                    cycle_count: intValue,
                                });
                            }}
                        />
                    </Col>
                </Form.Row>
            ) : (
                ""
            )}
        </>
    );
};

const ActuatorCount = () => {
    const {
        editedActuator: { cycle_count },
        setEditedActuator,
    }: {
        editedActuator: Actuator;
        setEditedActuator: React.Dispatch<React.SetStateAction<Actuator>>;
    } = useActuatorConfigurationContext();

    const [selectedLoopCycle, setSelectedLoopCycle] = useState(
        cycle_count ?? 0
    );

    const [loopCycleOption, setLoopCycleOption] = useState<
        typeof INFINITE | typeof COUNTED
    >(INFINITE);

    useEffect(() => {
        setEditedActuator((prevState) => ({
            ...prevState,
            cycle_count: loopCycleOption === INFINITE ? 0 : selectedLoopCycle,
        }));
    }, [selectedLoopCycle, loopCycleOption, setEditedActuator]);

    useEffect(() => {
        if (cycle_count)
            setLoopCycleOption(cycle_count === 0 ? INFINITE : COUNTED);
    }, [cycle_count]);

    return (
        <RawActuatorCount
            selectedLoopCycle={selectedLoopCycle}
            loopCycleOption={loopCycleOption}
            onChange={({ cycleOption, cycle_count }) => {
                if (cycleOption) {
                    setLoopCycleOption(cycleOption);
                }
                if (cycle_count !== undefined) {
                    setSelectedLoopCycle(Number(cycle_count));
                }
            }}
        />
    );
};

export default ActuatorCount;
